import React, { useState, useEffect } from 'react';
import { houseInfo, stayTypeOptions } from '../services/supabaseClient';
import { formatDate } from '../services/bookingAPI';
import { fetchPrice, formatPrice } from '../services/pricingService';

// Extra bed configuration by property
const extraBedConfig = {
  'pigertons': { maxBeds: 4, pricePerBed: 40, priceType: 'per_night' },
  'hayho_house': { maxBeds: 2, pricePerBed: 40, priceType: 'per_night' },
  'hesdin_hall': { maxBeds: 6, pricePerBed: 40, priceType: 'per_night' },
  'the_corn_crib': { maxBeds: 10, pricePerBed: 50, priceType: 'per_night' },
  'the_wealthy_merchant': { maxBeds: 2, pricePerBed: 200, priceType: 'entire_stay' },
  'lady_avas_tower': { maxBeds: 0, pricePerBed: 0, priceType: 'none' }
};

const BookingForm = ({ isOpen, onClose, selectedDate, selectedHouse, availableStayTypes = [] }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: initial, 2: details, 3: contact/payment
  const [formData, setFormData] = useState({
    stayFor: '',
    adults: 0,
    children: 0,
    infants: 0,
    extraBeds: 0,
    dogFee: 0,
    earlyCheckIn: 0,
    fireWoodBag: 0,
    welcomeCake: 0,
    groceryUnpacking: 0,
    bahamasPackage: 0,
    // Contact details
    title: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    postCode: '',
    country: '',
    region: '',
    email: '',
    telephone: '',
    mobile: '',
    // Other information
    howDidYouFind: '',
    whereDidYouHear: '',
    celebrating: '',
    pets: '',
    customBed: '',
    // Payment
    paymentMethod: '',
    agreeTerms: false,
    marketingEmails: false
  });
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        stayFor: '',
        adults: 0,
        children: 0,
        infants: 0,
        extraBeds: 0,
        dogFee: 0,
        earlyCheckIn: 0,
        fireWoodBag: 0,
        welcomeCake: 0,
        groceryUnpacking: 0,
        bahamasPackage: 0,
        // Contact details
        title: '',
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        postCode: '',
        country: '',
        region: '',
        email: '',
        telephone: '',
        mobile: '',
        // Other information
        howDidYouFind: '',
        whereDidYouHear: '',
        celebrating: '',
        pets: '',
        customBed: '',
        // Payment
        paymentMethod: '',
        agreeTerms: false,
        marketingEmails: false
      });
      setPrice(null);
      setPriceLoading(false);
      setCurrentStep(1);
    }
  }, [isOpen]);

  // Fetch price when stay duration changes
  useEffect(() => {
    const fetchPriceData = async () => {
      if (formData.stayFor && selectedDate && selectedHouse) {
        setPriceLoading(true);
        try {
          const priceData = await fetchPrice(selectedHouse, selectedDate, parseInt(formData.stayFor));
          setPrice(priceData);
        } catch (error) {
          console.error('Error fetching price:', error);
          setPrice(null);
        } finally {
          setPriceLoading(false);
        }
      } else {
        setPrice(null);
        setPriceLoading(false);
      }
    };

    fetchPriceData();
  }, [formData.stayFor, selectedDate, selectedHouse]);

  // Calculate total price including extras
  const calculateTotalPrice = () => {
    if (!price || !formData.stayFor) return null;
    
    let totalPrice = parseFloat(price);
    const nights = parseInt(formData.stayFor);
    
    // Add extra bed costs
    if (formData.extraBeds > 0 && selectedHouse) {
      const bedConfig = extraBedConfig[selectedHouse];
      if (bedConfig && bedConfig.maxBeds > 0) {
        if (bedConfig.priceType === 'per_night') {
          totalPrice += formData.extraBeds * bedConfig.pricePerBed * nights;
        } else if (bedConfig.priceType === 'entire_stay') {
          totalPrice += formData.extraBeds * bedConfig.pricePerBed;
        }
      }
    }
    
    // Add other extras
    totalPrice += formData.dogFee * 25.00 * nights; // £25.00 per night
    totalPrice += formData.earlyCheckIn * 120.00; // £120.00 each
    totalPrice += formData.fireWoodBag * 10.00; // £10.00 each
    totalPrice += formData.welcomeCake * 25.00; // £25.00 each
    totalPrice += formData.groceryUnpacking * 75.00; // £75.00 each
    totalPrice += formData.bahamasPackage * 120.00; // £120.00 each
    
    return totalPrice;
  };

  // Get maximum capacity for adults and children based on selected house
  const getMaxCapacity = () => {
    if (!selectedHouse || !houseInfo[selectedHouse]) return 20;
    return houseInfo[selectedHouse].capacity;
  };

  // Get extra bed options for selected house
  const getExtraBedOptions = () => {
    if (!selectedHouse) return [];
    const bedConfig = extraBedConfig[selectedHouse];
    if (!bedConfig || bedConfig.maxBeds === 0) return [];
    
    const options = [];
    for (let i = 0; i <= bedConfig.maxBeds; i++) {
      options.push(i);
    }
    return options;
  };

  // Get extra bed price description
  const getExtraBedPriceDescription = () => {
    if (!selectedHouse) return '';
    const bedConfig = extraBedConfig[selectedHouse];
    if (!bedConfig || bedConfig.maxBeds === 0) return 'Extra beds not available';
    
    if (bedConfig.priceType === 'per_night') {
      return `£${bedConfig.pricePerBed} per bed per night`;
    } else if (bedConfig.priceType === 'entire_stay') {
      return `£${bedConfig.pricePerBed} per mezzanine bed for entire stay`;
    }
    return '';
  };

  // Filter stay options based on available stay types
  const getAvailableStayOptions = () => {
    if (!availableStayTypes || availableStayTypes.length === 0) {
      return stayTypeOptions; // Show all if no specific types available
    }

    return stayTypeOptions.filter(option => 
      availableStayTypes.includes(option.type)
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContinue = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleCompleteBooking = async () => {
    try {
      // Validate required fields
      if (!formData.title || !formData.firstName || !formData.lastName || 
          !formData.address1 || !formData.city || !formData.postCode || 
          !formData.country || !formData.email || !formData.telephone || 
          !formData.mobile) {
        alert('Please fill in all required fields.');
        return;
      }

      // Calculate dates
      const checkInDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
      const checkOutDate = selectedDate ? 
        new Date(selectedDate.getTime() + (parseInt(formData.stayFor) * 24 * 60 * 60 * 1000))
          .toISOString().split('T')[0] : '';

      // Prepare booking data for Stripe
      const bookingData = {
        house: selectedHouse,
        houseName: houseDisplayInfo?.name,
        checkInDate: checkInDate,
        checkOutDate: checkOutDate,
        nights: parseInt(formData.stayFor),
        totalPrice: calculateTotalPrice(),
        bookingData: {
          ...formData,
          adults: formData.adults,
          children: formData.children,
          infants: formData.infants,
          extraBeds: formData.extraBeds,
          dogFee: formData.dogFee,
          earlyCheckIn: formData.earlyCheckIn,
          fireWoodBag: formData.fireWoodBag,
          welcomeCake: formData.welcomeCake,
          groceryUnpacking: formData.groceryUnpacking,
          bahamasPackage: formData.bahamasPackage
        }
      };

      console.log('Creating Stripe checkout session with data:', bookingData);

      // Create Stripe checkout session
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      console.log('Stripe API response:', data);

      if (data.status === 'success' && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        console.error('Stripe checkout session creation failed:', data);
        alert(`Failed to create payment session: ${data.message || 'Unknown error'}. Please try again.`);
      }

    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('An error occurred while processing your booking. Please try again.');
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    }
  };

  if (!isOpen) return null;

  const houseDisplayInfo = houseInfo[selectedHouse];
  const formattedDate = selectedDate ? formatDate(selectedDate) : '';
  const maxCapacity = getMaxCapacity();
  const extraBedOptions = getExtraBedOptions();
  const totalPrice = calculateTotalPrice();

  // Step 1: Initial booking form (prefilled window)
  if (currentStep === 1) {
    return (
      <div className="booking-modal-overlay" onClick={onClose}>
        <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
          <div className="booking-form">
            <h2>Your booking</h2>
            
            <div className="form-group">
              <label><strong>Selected property:</strong> {houseDisplayInfo?.name}</label>
            </div>

            <div className="form-group">
              <label><strong>Arrival:</strong> {formattedDate}</label>
            </div>

            <div className="form-group">
              <label htmlFor="stayFor"><strong>Stay for:</strong></label>
              <select 
                id="stayFor"
                value={formData.stayFor}
                onChange={(e) => handleInputChange('stayFor', e.target.value)}
              >
                <option value="">---</option>
                {getAvailableStayOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="guest-counters">
              <div className="counter-group">
                <label htmlFor="adults"><strong>Adults:</strong></label>
                <select 
                  id="adults"
                  value={formData.adults}
                  onChange={(e) => handleInputChange('adults', parseInt(e.target.value))}
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div className="counter-group">
                <label htmlFor="children"><strong>Children:</strong></label>
                <select 
                  id="children"
                  value={formData.children}
                  onChange={(e) => handleInputChange('children', parseInt(e.target.value))}
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div className="counter-group">
                <label htmlFor="infants"><strong>Infants:</strong></label>
                <select 
                  id="infants"
                  value={formData.infants}
                  onChange={(e) => handleInputChange('infants', parseInt(e.target.value))}
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price Display Section */}
            {formData.stayFor && (
              <div className="price-section">
                <div className="price-display">
                  <strong>Total Price: </strong>
                  {priceLoading ? (
                    <span className="price-loading">Calculating price...</span>
                  ) : (
                    <span className="price-amount">{formatPrice(price)}</span>
                  )}
                </div>
              </div>
            )}

            <button 
              className="book-now-btn" 
              onClick={handleContinue}
              disabled={!formData.stayFor || priceLoading}
            >
              CONTINUE
            </button>

            <div className="form-footer">
              <div className="footer-links">
                <span>Cookie policy</span>
                <span className="powered-by">Powered by</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Detailed booking modal with extras
  if (currentStep === 2) {
    return (
      <div className="booking-modal-overlay" onClick={onClose}>
        <div className="booking-modal booking-modal-detailed" onClick={(e) => e.stopPropagation()}>
          <div className="booking-form">
            <div className="booking-header">
              <button className="back-btn" onClick={handleBack}>
                ← Your booking
              </button>
            </div>

          <div className="booking-content">
            <div className="booking-summary">
              <h3>{houseDisplayInfo?.name}</h3>
              <div className="booking-dates">
                <p>{formattedDate}</p>
                <p>to {selectedDate ? formatDate(new Date(selectedDate.getTime() + (parseInt(formData.stayFor) * 24 * 60 * 60 * 1000))) : ''} ({formData.stayFor} nights)</p>
              </div>
              
              {houseDisplayInfo?.image && (
                <div className="property-image">
                  <img src={houseDisplayInfo.image} alt={houseDisplayInfo.name} />
                </div>
              )}
            </div>

            <div className="guest-selection">
              <div className="guest-row">
                <label htmlFor="adults-detailed"><strong>Adults:</strong></label>
                <select 
                  id="adults-detailed"
                  value={formData.adults}
                  onChange={(e) => handleInputChange('adults', parseInt(e.target.value))}
                >
                  {[...Array(maxCapacity + 1)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="capacity-info">Property sleeps a total of {maxCapacity}</span>
              </div>

              <div className="guest-row">
                <label htmlFor="children-detailed"><strong>Children:</strong></label>
                <select 
                  id="children-detailed"
                  value={formData.children}
                  onChange={(e) => handleInputChange('children', parseInt(e.target.value))}
                >
                  {[...Array(maxCapacity + 1)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div className="guest-row">
                <label htmlFor="infants-detailed"><strong>Infants:</strong></label>
                <select 
                  id="infants-detailed"
                  value={formData.infants}
                  onChange={(e) => handleInputChange('infants', parseInt(e.target.value))}
                >
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="infants-info">Children aged up to 2 years</span>
              </div>
            </div>

            <div className="pricing-section">
              <div className="price-row">
                <span>Full rate:</span>
                <span className="price">{formatPrice(price)}</span>
              </div>
            </div>

            <div className="extras-section">
              <h4>Extras</h4>
              <h5>CONFIGURE YOUR STAY</h5>
              
              {extraBedOptions.length > 0 && (
                <div className="extra-row">
                  <label htmlFor="extraBeds">Extra Bed</label>
                  <select 
                    id="extraBeds"
                    value={formData.extraBeds}
                    onChange={(e) => handleInputChange('extraBeds', parseInt(e.target.value))}
                  >
                    {extraBedOptions.map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                  <span className="extra-price">{getExtraBedPriceDescription()}</span>
                  <span className="extra-total">
                    {formData.extraBeds > 0 ? formatPrice(
                      extraBedConfig[selectedHouse]?.priceType === 'per_night' 
                        ? formData.extraBeds * extraBedConfig[selectedHouse].pricePerBed * parseInt(formData.stayFor)
                        : formData.extraBeds * extraBedConfig[selectedHouse].pricePerBed
                    ) : '£ 0.00'}
                  </span>
                </div>
              )}

              {extraBedOptions.length === 0 && (
                <div className="extra-row disabled">
                  <label>Extra Bed</label>
                  <span className="not-available">❌ Extra beds not available</span>
                </div>
              )}

              <div className="extra-row">
                <label htmlFor="dogFee">Dog Fee</label>
                <select 
                  id="dogFee"
                  value={formData.dogFee}
                  onChange={(e) => handleInputChange('dogFee', parseInt(e.target.value))}
                >
                  {[0, 1, 2].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="extra-price">£ 25.00 per night</span>
                <span className="extra-total">
                  {formatPrice(formData.dogFee * 25.00 * parseInt(formData.stayFor || 0))}
                </span>
              </div>

              <div className="extra-row">
                <label htmlFor="earlyCheckIn">Early Check In</label>
                <select 
                  id="earlyCheckIn"
                  value={formData.earlyCheckIn}
                  onChange={(e) => handleInputChange('earlyCheckIn', parseInt(e.target.value))}
                >
                  {[0, 1].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="extra-price">£ 120.00 each</span>
                <span className="extra-total">
                  {formatPrice(formData.earlyCheckIn * 120.00)}
                </span>
              </div>

              <h5>SPECIAL TOUCHES</h5>

              <div className="extra-row">
                <label htmlFor="fireWoodBag">Fire Wood Bag</label>
                <select 
                  id="fireWoodBag"
                  value={formData.fireWoodBag}
                  onChange={(e) => handleInputChange('fireWoodBag', parseInt(e.target.value))}
                >
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="extra-price">£ 10.00 each</span>
                <span className="extra-total">
                  {formatPrice(formData.fireWoodBag * 10.00)}
                </span>
              </div>

              <div className="extra-row">
                <label htmlFor="welcomeCake">Welcome Cake</label>
                <select 
                  id="welcomeCake"
                  value={formData.welcomeCake}
                  onChange={(e) => handleInputChange('welcomeCake', parseInt(e.target.value))}
                >
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="extra-price">£ 25.00 each</span>
                <span className="extra-total">
                  {formatPrice(formData.welcomeCake * 25.00)}
                </span>
              </div>

              <div className="extra-row">
                <label htmlFor="groceryUnpacking">Grocery Unpacking</label>
                <select 
                  id="groceryUnpacking"
                  value={formData.groceryUnpacking}
                  onChange={(e) => handleInputChange('groceryUnpacking', parseInt(e.target.value))}
                >
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="extra-price">£ 75.00 each</span>
                <span className="extra-total">
                  {formatPrice(formData.groceryUnpacking * 75.00)}
                </span>
              </div>

              <div className="extra-row">
                <label htmlFor="bahamasPackage">Bahamas Package (add how many nights you're staying!)</label>
                <select 
                  id="bahamasPackage"
                  value={formData.bahamasPackage}
                  onChange={(e) => handleInputChange('bahamasPackage', parseInt(e.target.value))}
                >
                  {[...Array(11)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
                <span className="extra-price">£ 120.00 each</span>
                <span className="extra-total">
                  {formatPrice(formData.bahamasPackage * 120.00)}
                </span>
              </div>
            </div>

            <div className="total-section">
              <div className="total-row">
                <span><strong>Total:</strong></span>
                <span className="total-price">
                  {priceLoading ? 'Calculating...' : formatPrice(totalPrice)}
                </span>
              </div>
            </div>

            <button className="book-now-btn" onClick={handleContinue}>
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    </div>
    );
  }

  // Step 3: Contact details and payment modal
  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal booking-modal-final" onClick={(e) => e.stopPropagation()}>
        <div className="booking-form">
          <div className="booking-header">
            <button className="back-btn" onClick={handleBack}>
              ← Your booking
            </button>
          </div>

          <div className="booking-content-final">
            <div className="booking-summary-sidebar">
              <h4>Booking summary</h4>
              
              {houseDisplayInfo?.image && (
                <div className="property-image-small">
                  <img src={houseDisplayInfo.image} alt={houseDisplayInfo.name} />
                </div>
              )}

              <h5>{houseDisplayInfo?.name}</h5>
              <div className="booking-dates-small">
                <p>{formattedDate}</p>
                <p>to {selectedDate ? formatDate(new Date(selectedDate.getTime() + (parseInt(formData.stayFor) * 24 * 60 * 60 * 1000))) : ''} ({formData.stayFor} nights)</p>
              </div>

              <div className="guest-summary">
                <p><strong>Adults:</strong> {formData.adults} <strong>Children:</strong> {formData.children} <strong>Infants:</strong> {formData.infants}</p>
              </div>

              <div className="price-breakdown">
                <div className="price-row-small">
                  <span>Full rate:</span>
                  <span>£ {parseFloat(price || 0).toFixed(2)}</span>
                </div>

                <h6>CONFIGURE YOUR STAY</h6>
                <h6>SPECIAL TOUCHES</h6>

                <div className="price-row-small">
                  <span>Deposit:</span>
                  <span>£ 0.00</span>
                </div>
                <div className="price-row-small">
                  <span>Balance:</span>
                  <span>Due now</span>
                  <span>£ {(calculateTotalPrice() || 0).toFixed(2)}</span>
                </div>

                <div className="total-row-small">
                  <span><strong>Total:</strong></span>
                  <span><strong>£ {(calculateTotalPrice() || 0).toFixed(2)}</strong></span>
                </div>

                <div className="security-deposit">
                  <p><strong>Security deposit</strong> £750.00</p>
                  <p>We ask our guests for a security deposit, pre-authorised against your credit card. This is in case there is any damage to the property during your stay. The money is simply reserved and then released afterwards if there is no damage. We will contact you nearer the time of your arrival to arrange this.</p>
                </div>
              </div>
            </div>

            <div className="booking-main-form">
              <div className="contact-details-section">
                <h4>Contact Details</h4>
                
                <div className="form-row">
                  <div className="form-field">
                    <select 
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                    >
                      <option value="">Title *</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <input 
                      type="text" 
                      placeholder="First Name *"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <input 
                      type="text" 
                      placeholder="Last Name *"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field full-width">
                    <input 
                      type="text" 
                      placeholder="Address 1 *"
                      value={formData.address1}
                      onChange={(e) => handleInputChange('address1', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field full-width">
                    <input 
                      type="text" 
                      placeholder="Address 2"
                      value={formData.address2}
                      onChange={(e) => handleInputChange('address2', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <input 
                      type="text" 
                      placeholder="City / Town *"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <input 
                      type="text" 
                      placeholder="Post Code *"
                      value={formData.postCode}
                      onChange={(e) => handleInputChange('postCode', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <select 
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    >
                      <option value="">Country *</option>
                      <option value="UK">United Kingdom</option>
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <input 
                      type="text" 
                      placeholder="County / Region / State"
                      value={formData.region}
                      onChange={(e) => handleInputChange('region', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field full-width">
                    <input 
                      type="email" 
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <input 
                      type="tel" 
                      placeholder="Telephone *"
                      value={formData.telephone}
                      onChange={(e) => handleInputChange('telephone', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <input 
                      type="tel" 
                      placeholder="Mobile Telephone *"
                      value={formData.mobile}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                    />
                  </div>
                </div>
              </div>


              <button 
                className="book-now-btn complete-booking-btn" 
                onClick={handleCompleteBooking}
              >
                COMPLETE BOOKING
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
